import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import { DonorIdentityPicker, refMatchesCandidate } from './pregnancyEditor';
import { matchesMemberRef } from '../../pedigree/liveBirthInheritance';

// TGCLOUD-1613 — record a gamete donor against someone who has ALREADY BEEN BORN.
//
// Donors used to be reachable only through the pregnancy workflow. Kathryn's objection on the call:
// where a family presents after the children are born, the user would have to create a pregnancy and
// outcome it purely to get the donors onto the pedigree, "which is not acceptable". It would also be
// wrong — gravidity and parity derive from the mother's pregnancy records, so a placeholder
// pregnancy silently inflates her obstetric history.
//
// The link is stored on the RECIPIENT as `gameteDonors: [{ role, memberId }]`, mirroring the way an
// adopted-in person carries bioFatherId/bioMotherId: a biological link to someone who is not a
// rearing parent. One data direction, and the render derivation turns it into exactly the same node
// and line as a pregnancy-stage donor.
//
// TWO DIRECTIONS IN THE UI, THOUGH, and that is deliberate. Tim's QA on G-1-01491 recorded two donors
// backwards — he selected the person he thought of as the donor and picked the recipient from the
// list, which stored the exact inverse. The old title ("Gamete donor for Person 8") reads just as
// naturally as "give Person 8 a donor role", and the drawn line is identical either way, so nothing
// on the chart contradicted him. Rather than train users out of that instinct:
//
//   mode 'received' — "who was THIS person's donor?"        (writes onto this person)
//   mode 'donated'  — "who did THIS person donate to?"      (writes onto the person chosen)
//
// Both end in the same stored shape, and both show a plain-English sentence of what is about to be
// recorded, so the direction is confirmed in words before anything is saved.

const ROLE_LABELS = [
  ['sperm', 'Sperm donor'],
  ['ovum', 'Egg donor'],
  ['embryo', 'Donated embryo']
];

export const roleLabelOf = (role) =>
  ROLE_LABELS.find(([key]) => key === role)?.[1] || role;

const VERB = { sperm: 'sperm', ovum: 'an egg', embryo: 'an embryo' };

export const nameOfMember = (m) =>
  (m && [m.firstName, m.lastName].filter(Boolean).join(' ')) ||
  (m ? `Person ${m.pid}` : 'someone no longer in this family');

// The reference to WRITE for a member. Prefer the uid: it exists before the pedigree has saved,
// whereas _id is minted server-side. refMatchesCandidate recognises any of the three afterwards.
export const writeRefFor = (m) => (m ? m.uid || m._id || m.pid : null);

// Every link where `person` is the DONOR — scanned across the family, because the link lives on the
// recipient. Used for the "already recorded" list in 'donated' mode, and it is the only way to see
// what a person has donated.
export const linksDonatedBy = (person, memberRecords) => {
  const out = [];
  (memberRecords || []).forEach((m) => {
    (Array.isArray(m.gameteDonors) ? m.gameteDonors : []).forEach(
      (d, index) => {
        if (d && matchesMemberRef(person, d.memberId)) {
          out.push({ recipient: m, role: d.role, index });
        }
      }
    );
  });
  return out;
};

export default function GameteDonorModal({
  person,
  memberRecords = [],
  mode = 'received',
  onAddLink,
  onRemoveLink,
  onClose,
  onCreateDonorPerson,
  busy
}) {
  const donatedMode = mode === 'donated';
  const [role, setRole] = React.useState('sperm');
  const [otherRef, setOtherRef] = React.useState(null);

  const personName = nameOfMember(person);

  // Anyone in the family except the person themselves — nobody is their own donor, in either
  // direction.
  const candidates = React.useMemo(
    () =>
      (memberRecords || [])
        .filter((m) => m && m.pid !== person?.pid)
        .map((m) => ({
          ref: writeRefFor(m),
          _id: m._id,
          uid: m.uid,
          pid: m.pid,
          gender: m.gender,
          label: nameOfMember(m)
        })),
    [memberRecords, person]
  );

  // What is already on record, in whichever direction this modal is showing.
  const existing = React.useMemo(() => {
    if (donatedMode) {
      return linksDonatedBy(person, memberRecords).map((l) => ({
        recipientPid: l.recipient.pid,
        index: l.index,
        label: `${roleLabelOf(l.role)} → ${nameOfMember(l.recipient)}`
      }));
    }
    return (Array.isArray(person?.gameteDonors) ? person.gameteDonors : []).map(
      (d, index) => ({
        recipientPid: person.pid,
        index,
        label: `${roleLabelOf(d.role)} — ${
          d.memberId
            ? candidates.find((c) => refMatchesCandidate(d.memberId, c))
                ?.label || 'a person no longer in this family'
            : 'anonymous'
        }`
      })
    );
  }, [donatedMode, person, memberRecords, candidates]);

  const otherName =
    candidates.find((c) => refMatchesCandidate(otherRef, c))?.label || null;

  // The sentence that makes the direction unmistakable before anything is written.
  const summary = donatedMode
    ? otherName
      ? `${personName} donated ${VERB[role]} to ${otherName}.`
      : `Choose who ${personName} donated to.`
    : otherName
    ? `${otherName} donated ${VERB[role]} to ${personName}.`
    : `${personName} was conceived using ${VERB[role]} from an anonymous donor.`;

  // 'donated' mode REQUIRES a recipient — an anonymous one would be a link to nobody. 'received'
  // mode does not: an anonymous donor is the common case and draws a satellite.
  const canSave = donatedMode ? otherRef != null : true;

  const handleSave = async () => {
    if (!canSave) return;
    if (donatedMode) {
      const recipient = (memberRecords || []).find((m) =>
        refMatchesCandidate(otherRef, {
          ref: writeRefFor(m),
          _id: m._id,
          uid: m.uid,
          pid: m.pid
        })
      );
      if (!recipient) return;
      await onAddLink?.({
        recipientPid: recipient.pid,
        role,
        donorRef: writeRefFor(person)
      });
      return;
    }
    await onAddLink?.({
      recipientPid: person.pid,
      role,
      donorRef: otherRef ? String(otherRef) : null
    });
  };

  return (
    <CustomDialog
      open={true}
      handleClose={onClose}
      allowBackgroundClose={onClose}
      showActions={false}
      title={
        donatedMode
          ? `Record ${personName} as a gamete donor`
          : `Record a gamete donor for ${personName}`
      }
      minHeight={280}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {donatedMode
            ? `Records that ${personName} DONATED gametes used to conceive someone else in this family.`
            : `Records that ${personName} WAS CONCEIVED using donor gametes.`}{' '}
          No pregnancy is created — this is a biological link, drawn as a line
          between the two people.
        </Typography>

        {existing.length > 0 && (
          <Box>
            <FormLabel sx={{ fontSize: '0.85rem' }}>
              {donatedMode ? 'Already donated' : 'Already recorded'}
            </FormLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
              {existing.map((e) => (
                <Chip
                  key={`${e.recipientPid}-${e.index}`}
                  size="small"
                  label={e.label}
                  onDelete={
                    busy
                      ? undefined
                      : () =>
                          onRemoveLink?.({
                            recipientPid: e.recipientPid,
                            index: e.index
                          })
                  }
                  sx={{ borderRadius: '4px' }}
                />
              ))}
            </Box>
          </Box>
        )}

        <FormControl>
          <FormLabel sx={{ fontSize: '0.85rem' }}>What was donated?</FormLabel>
          <RadioGroup
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              // A person chosen for one gamete is rarely right for another — clear rather than
              // silently carry the selection across.
              setOtherRef(null);
            }}
          >
            {ROLE_LABELS.map(([key, label]) => (
              <FormControlLabel
                key={key}
                value={key}
                control={<Radio size="small" />}
                label={label}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <DonorIdentityPicker
          role={role}
          label={
            donatedMode
              ? `Who was conceived using ${personName}'s donation`
              : roleLabelOf(role)
          }
          promptOverride={
            donatedMode
              ? `Who did ${personName} donate to?`
              : `${roleLabelOf(role)} — who was it?`
          }
          // In 'donated' mode there is nobody to be anonymous: the donor is the selected person and
          // the recipient must be a named individual on this pedigree.
          allowAnonymous={!donatedMode}
          memberId={otherRef}
          onChange={setOtherRef}
          candidates={candidates}
          onCreateDonorPerson={donatedMode ? undefined : onCreateDonorPerson}
          disabled={busy}
        />

        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {summary}
        </Typography>

        <ModalButtons
          onClick={handleSave}
          handleClose={onClose}
          addLabel="Add donor link"
          hideContinueAdding
          saveButtonProps={{ disabled: !canSave || busy }}
        />
      </Box>
    </CustomDialog>
  );
}
